import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FaCheckCircle, FaCog, FaTachometerAlt, FaTools } from "react-icons/fa";
import "./ECURemapping.css";
import heroImage from "../assets/Ecu.webp";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const ECURemapping = () => {
  const [selectedServices, setSelectedServices] = useState([]);
  const navigate = useNavigate();

  const services = [
    { id: 1, name: "Stage 1", description: "Performance tune for increased power and torque", category: "Performance" },
    { id: 2, name: "DPF solutions", description: "Diesel Particulate Filter management and solutions", category: "Emissions" },
    { id: 3, name: "EGR solutions", description: "Exhaust Gas Recirculation system optimization", category: "Emissions" },
    { id: 4, name: "Adblue/SRC solutions", description: "AdBlue system management and solutions", category: "Emissions" },
    { id: 5, name: "MAF solutions", description: "Mass Air Flow sensor optimization", category: "Sensors" },
    { id: 6, name: "O2/Lambda solutions", description: "Oxygen sensor management and tuning", category: "Sensors" },
    { id: 7, name: "IMMO solutions", description: "Immobilizer system solutions", category: "Security" },
    { id: 8, name: "TVA solutions", description: "Throttle Valve Actuator optimization", category: "Systems" },
    { id: 9, name: "HardCut", description: "Hard cut limiter for launch control", category: "Limiters" },
    { id: 10, name: "Full Emission Solutions", description: "Complete emissions system removal", category: "Emissions" },
    { id: 11, name: "DTC solutions", description: "Diagnostic Trouble Code management", category: "Diagnostics" },
    { id: 12, name: "AGS Solutions", description: "Active Grille Shutter removal", category: "Systems" },
    { id: 13, name: "BMS/BPCM Recovery", description: "Battery Management System recovery", category: "Recovery" },
    { id: 14, name: "Boost Sensor Calibration", description: "Turbo boost pressure sensor calibration", category: "Calibration" },
    { id: 15, name: "Burbles Activation", description: "Exhaust burble and pop sounds activation", category: "Sound" },
    { id: 16, name: "Cold Start Solutions", description: "Cold start emissions management removal", category: "Start Systems" },
    { id: 17, name: "Ewap Solutions", description: "Evaporative emissions system removal", category: "Emissions" },
    { id: 18, name: "Exhaust Flap Solutions", description: "Exhaust valve flap removal", category: "Exhaust" },
    { id: 19, name: "Flaps/Swirl Solutions", description: "Intake manifold flaps removal", category: "Intake" },
    { id: 20, name: "GPF/OPF Solutions", description: "Gasoline/Otto Particulate Filter removal", category: "Emissions" },
    { id: 21, name: "Hot Starts Fix", description: "Hot start issue resolution", category: "Start Systems" },
    { id: 22, name: "Launch Control", description: "Launch control system activation", category: "Performance" },
    { id: 23, name: "PopCorn / PapBang", description: "Exhaust pop and bang sounds", category: "Sound" },
    { id: 24, name: "Sap Solutions", description: "Secondary Air Pump removal", category: "Systems" },
    { id: 25, name: "Speed Limiter Vmax Solutions", description: "Maximum speed limiter removal", category: "Limiters" },
    { id: 26, name: "Start Stop Disable", description: "Auto start-stop system disable", category: "Comfort" },
    { id: 27, name: "Tork Monitor Disable", description: "Torque monitoring system disable", category: "Safety" },
    { id: 28, name: "Nox Solutions", description: "NOx sensor and system removal", category: "Emissions" },
    { id: 29, name: "IAT Solutions", description: "Intake Air Temperature sensor removal", category: "Sensors" },
    { id: 30, name: "Water Pump Solutions", description: "Electric water pump management", category: "Cooling" },
    { id: 31, name: "Catalyst Solutions", description: "Catalytic converter removal coding", category: "Emissions" },
    { id: 32, name: "Sport Display Calibration", description: "Sport mode display calibration", category: "Calibration" },
    { id: 33, name: "Readiness Calibration", description: "Emissions readiness monitor calibration", category: "Calibration" },
    { id: 34, name: "Rev Limiter Solutions", description: "RPM limiter removal or adjustment", category: "Limiters" },
    { id: 35, name: "Oil Pressure Fix", description: "Oil pressure sensor issue resolution", category: "Engine" },
    { id: 36, name: "TPROT Solutions", description: "Transmission protection removal", category: "Protection" },
    { id: 37, name: "Kickdown Solutions", description: "Kickdown function modification", category: "Transmission" },
    { id: 38, name: "Flex E85", description: "E85 fuel compatibility tuning", category: "Fuel" },
    { id: 39, name: "ACT Solutions", description: "Active Cylinder Technology removal", category: "Systems" },
  ];

  const handleToggle = (service) => {
    const isSelected = selectedServices.find((s) => s.id === service.id);
    if (isSelected) {
      setSelectedServices(selectedServices.filter((s) => s.id !== service.id));
    } else {
      setSelectedServices([...selectedServices, service]);
    }
  };

  const handleRequestQuote = () => {
    if (selectedServices.length === 0) return;

    const serviceNames = selectedServices.map((service) => service.name).join(", ");
    const subject = `ECU Remapping Service Request - ${serviceNames}`;
    const message = `Hello,\n\nI am interested in the following ECU Remapping services:\n\n${selectedServices
      .map(
        (service) =>
          `• ${service.name} (${service.category})\n  ${service.description}`
      )
      .join(
        "\n\n"
      )}\n\nPlease provide detailed information about pricing and availability.\n\nThank you!`;

    navigate("/contact", {
      state: { subject, message },
    });
  };

  const canonical = "https://carforensic.uk/ecu-remapping";

  const ecuServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "ECU Remapping and Tuning",
    "provider": {
      "@type": "AutoRepair",
      "name": "Car Forensics LTD",
      "url": "https://carforensic.uk/",
      "telephone": "+447501911970",
      "email": "carforensicsltd@gmail.com",
      "areaServed": [
        "Kent",
        "Maidstone",
        "Gillingham",
        "Sevenoaks",
        "Sittingbourne"
      ]
    },
    "areaServed": [
      "Kent",
      "Maidstone",
      "Gillingham",
      "Sevenoaks",
      "Sittingbourne"
    ],
    "url": canonical,
    "description":
      "Professional ECU remapping and tuning services across Kent including Maidstone, Gillingham, Sevenoaks and Sittingbourne."
  };

  return (
    <>
      <SEO
        title="Car Forensics LTD | ECU Remapping & Tuning"
        description="Professional ECU remapping and tuning services across Kent — Maidstone, Gillingham, Sevenoaks and Sittingbourne. Select the services you need and request a quote from Car Forensics LTD."
        canonical={canonical}
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(ecuServiceSchema)}
        </script>
      </Helmet>

      <div className="ecu-remapping-page">
        {/* Hero Section */}
        <section
          className="ecu-hero"
          style={{ backgroundImage: `url(${heroImage})` }}
        >
          <div className="heros-overlay"></div>
          <div className="ecu-hero-content">
            <div className="hero-icon-wrapper">
              <FaCog className="hero-icon rotating" />
            </div>
            <h1>ECU Remapping & Tuning Services</h1>
            <p>Professional engine optimization and performance enhancement</p>
            <div className="hero-stats">
              <div className="stat-item">
                <FaTachometerAlt />
                <span>15+ Years Experience</span>
              </div>
              <div className="stat-item">
                <FaTools />
                <span>Latest Equipment</span>
              </div>
              <div className="stat-item">
                <FaCheckCircle />
                <span>Guaranteed Results</span>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="ecu-services-section">
          <div className="ecu-container">
            <div className="section-intro">
              <h2>Select Your Required Services</h2>
              <p>
                Choose from our ECU tuning and diagnostic services<br></br>
                Select what you need and request a quote
              </p>
            </div>

            <div className="ecu-content-wrapper">
              <div className="services-selection-area">
                <div className="services-grid-ecu">
                  {services.map((service) => {
                    const isSelected = selectedServices.find(
                      (s) => s.id === service.id
                    );
                    return (
                      <label
                        key={service.id}
                        className={`service-checkbox-card ${
                          isSelected ? "selected" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={!!isSelected}
                          onChange={() => handleToggle(service)}
                        />
                        <div className="checkbox-custom">
                          <FaCheckCircle className="check-icon" />
                        </div>
                        <div className="servicess-info">
                          <div className="service-details-wrapper">
                            <span className="serv-name">{service.name}</span>
                            <span className="service-category-tag">
                              {service.category}
                            </span>
                          </div>
                          <p className="serv-description">
                            {service.description}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="summary-sidebar">
                <div className="summary-card">
                  <h3 className="summary-title">
                    <FaCheckCircle className="summary-icon" />
                    Selected Services
                  </h3>

                  {selectedServices.length === 0 ? (
                    <div className="empty-state">
                      <p>No services selected yet</p>
                      <span>Select services to see your selection</span>
                    </div>
                  ) : (
                    <>
                      <div className="selected-services-list">
                        {selectedServices.map((service) => (
                          <div
                            key={service.id}
                            className="selected-service-item"
                          >
                            <div className="service-detail">
                              <span className="item-name">{service.name}</span>
                              <span className="item-category">
                                {service.category}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="summary-divider"></div>

                      <div className="service-count-section">
                        <div className="count-row">
                          <span>Total Selected Services</span>
                          <span className="count-number">
                            {selectedServices.length}
                          </span>
                        </div>
                      </div>

                      <button
                        className="cta-button-ecu"
                        onClick={handleRequestQuote}
                      >
                        <span>Request Quote</span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </button>

                      <div className="info-note">
                        <p>
                          Selected services will be included in your quote
                          request. Contact us for pricing and availability.
                        </p>
                      </div>
                    </>
                  )}
                </div>

                <div className="contact-quick-card">
                  <h4>Need Assistance?</h4>
                  <p>Our experts are here to help</p>
                  <a href="tel:+447501911970" className="phone-link-ecu">
                    📞 +447501911970
                  </a>

                  {/* Internal link FIX (no reload) */}
                  <Link to="/contact" className="contact-link-ecu">
                    Contact Us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="ecu-info-section">
          <div className="ecu-container">
            <div className="infoss-grid">
              <div className="infoss-card">
                <div className="infoss-icon">
                  <FaCheckCircle />
                </div>
                <h3>Professional Service</h3>
                <p>
                  Certified technicians with extensive experience in ECU
                  programming and engine optimization.
                </p>
              </div>
              <div className="infoss-card">
                <div className="infoss-icon">
                  <FaTachometerAlt />
                </div>
                <h3>Performance Gains</h3>
                <p>
                  Unlock your vehicle&apos;s true potential with optimized power
                  delivery and improved fuel efficiency.
                </p>
              </div>
              <div className="infoss-card">
                <div className="infoss-icon">
                  <FaTools />
                </div>
                <h3>Latest Technology</h3>
                <p>
                  State-of-the-art equipment and software ensure safe, reliable,
                  and reversible modifications.
                </p>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default ECURemapping;
